import React, { createContext, useContext, useState, useEffect } from "react";
import { useAuth } from "./AuthContext";
import { CLEAR_WORKING_TABLES_EVENT } from "../utils/clearWorkingTables";

const AppContext = createContext(null);

export function AppProvider({ children }) {
  const { currentUser } = useAuth();

  const [tableData, setTableData] = useState(
    JSON.parse(localStorage.getItem("tableData") || "[]")
  );

  // Retest / Review clears localStorage table caches; drop in-memory copy too.
  useEffect(() => {
    const onClear = () => setTableData([]);
    window.addEventListener(CLEAR_WORKING_TABLES_EVENT, onClear);
    return () => window.removeEventListener(CLEAR_WORKING_TABLES_EVENT, onClear);
  }, []);
  const [rubricText, setRubricText] = useState("");
  const [pdfRubric, setPdfRubric] = useState(() => {
    const saved = localStorage.getItem("rubricPdfData");
    return saved ? JSON.parse(saved) : null;
  });
  const [message, setMessage] = useState({ show: false, text: '', type: 'info' });
  const [recheckRequest, setRecheckRequest] = useState(null);

  const addCSVData = (newRows) => {
    setTableData((prevData) => {
      const updated = [...prevData, ...newRows];
      localStorage.setItem("tableData", JSON.stringify(updated));
      return updated;
    });
  };

  const clearData = () => {
    setTableData([]);
    localStorage.removeItem("tableData");
  };

  const updateTableData = (updated) => {
    setTableData(updated);
    localStorage.setItem("tableData", JSON.stringify(updated));
  };

  const saveRubric = async (text, metadata = {}) => {
    if (!currentUser) return;
    if (!text || text.trim() === "") return;

    try {
      const API_BASE_URL = window.API_BASE_URL;
      const token = localStorage.getItem('authToken');

      const rubricData = {
        class_subject_id: metadata.classSubjectId,
        class: metadata.class,
        subject: metadata.subject,
        content: text,
        test_date: metadata.validUntil || null,
        issues_text: null,
        issues_json: null,
        // 'subjective' is the historical default — preserves existing behavior
        // for any caller that hasn't been updated to pass `type` yet.
        type: metadata.type || 'subjective',
        // Optional folder label shown on every folder card. Trimmed to NULL
        // server-side when blank.
        assignment_name: metadata.assignmentName || null,
        paper_type: metadata.paperType || null,
        // Optional: examiners / admins use this to create a rubric on behalf
        // of a teacher. Backend ignores it for teacher accounts.
        ...(metadata.createdByUsername ? { created_by_username: metadata.createdByUsername } : {}),
        // Set after the teacher confirms they want a second rubric for the
        // same class + subject + type + date.
        ...(metadata.allowSecond ? { allow_second: true } : {})
      };

      const response = await fetch(`${API_BASE_URL}/rubrics`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(rubricData)
      });

      const data = await response.json();

      if (!response.ok) {
        // Backend returns 409 when a matching class+subject+type+date rubric
        // already exists (see Rubric.create in models/rubric.model.js).
        // CONFIRM_SECOND_RUBRIC = one exists, teacher must confirm a second.
        // DUPLICATE_RUBRIC = two already exist (cap). Surface as a typed
        // error so the caller can show DuplicateRubricDialog instead of a
        // generic toast.
        if (response.status === 409) {
          const dupErr = new Error(data.message || 'A rubric with the same name and date already exists.');
          dupErr.code = data.code || 'DUPLICATE_RUBRIC';
          dupErr.existingId = data.existingId || null;
          dupErr.existingCount = data.existingCount || null;
          throw dupErr;
        }
        throw new Error(data.error || data.message || 'Failed to save rubric');
      }

      return data.rubric;
    } catch (error) {
      // Don't show the generic red toast for the duplicate / confirm-second
      // cases — the caller pops the DuplicateRubricDialog instead.
      const skipToast = error && (error.code === 'DUPLICATE_RUBRIC' || error.code === 'CONFIRM_SECOND_RUBRIC');
      if (!skipToast) {
        setMessage({ show: true, text: 'Failed to save rubric: ' + error.message, type: 'error' });
      }
      throw error;
    }
  };

  const handleRecheckStudent = (imageNames, folderPath) => {
    setRecheckRequest({ imageNames, folderPath });
  };

  const clearRecheckRequest = () => {
    setRecheckRequest(null);
  };

  // Load active rubric from API when user logs in
  useEffect(() => {
    if (!currentUser) return;

    const loadActiveRubric = async () => {
      try {
        const API_BASE_URL = window.API_BASE_URL;
        const token = localStorage.getItem('authToken');

        const response = await fetch(`${API_BASE_URL}/rubrics/active`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.rubric) {
            setRubricText(data.rubric.content);
            setPdfRubric(null);
            return;
          }
        }
      } catch (error) {
        console.log('No active rubric found, checking localStorage');
      }

      // Fallback to localStorage if no active rubric in database
      const localPdfData = localStorage.getItem("rubricPdfData");
      if (localPdfData) {
        setPdfRubric(JSON.parse(localPdfData));
        setRubricText("");
      } else {
        const localRubricText = localStorage.getItem("rubricText");
        if (localRubricText) {
          setRubricText(localRubricText);
        }
      }
    };

    loadActiveRubric();
  }, [currentUser]);

  return (
    <AppContext.Provider value={{
      tableData,
      rubricText,
      pdfRubric,
      message,
      recheckRequest,
      setMessage,
      addCSVData,
      clearData,
      updateTableData,
      saveRubric,
      setRubricText,
      setPdfRubric,
      handleRecheckStudent,
      clearRecheckRequest,
      currentUser
    }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useApp must be used within an AppProvider");
  }
  return context;
}
